import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Settings2, Users, Package } from "lucide-react";
import { toast } from "sonner";
import ConfirmDelete from "@/components/ConfirmDelete";
import { deleteWithUndo } from "@/lib/deleteWithUndo";

const empty = { key: "", label: "", type: "text", required: false, options: "" };

export default function CustomerFields() {
  const { user } = useAuth();
  const [entity, setEntity] = useState("customer");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const { data } = await api.get(`/fields/${entity}`);
    setRows(data);
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user, entity]);
  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        key: form.key,
        label: form.label,
        type: form.type,
        required: form.required,
        options: form.type === "dropdown" ? form.options.split(",").map((x) => x.trim()).filter(Boolean) : [],
      };
      await api.post(`/fields/${entity}`, payload);
      toast.success("Field added");
      setOpen(false); setForm(empty); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const del = (f) =>
    deleteWithUndo({
      deletePath: `/fields/${entity}/${f.id}`,
      restorePath: `/fields/${entity}/${f.id}/restore`,
      label: f.label,
      entityLabel: `${entity} field`,
      onChange: load,
    });

  return (
    <div className="p-8 lg:p-10 max-w-[1200px]">
      <PageHeader
        title="Custom Fields"
        subtitle="Customize the data captured for customers and products. Add text, number, date, or dropdown fields."
        testid="page-fields-header"
        action={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(empty); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-field-btn" className="bg-[#39FF14] hover:bg-[#2ECC71]">
                <Plus size={16} className="mr-2" /> Add Field
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">New Custom Field</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div><Label>Label*</Label><Input required data-testid="field-label-input" placeholder="e.g. GST Number" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value, key: form.key || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "_") })} /></div>
                <div><Label>Key (system name)</Label><Input required data-testid="field-key-input" placeholder="e.g. gst_number" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} /></div>
                <div>
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="field-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="text">Text</SelectItem>
                      <SelectItem value="number">Number</SelectItem>
                      <SelectItem value="date">Date</SelectItem>
                      <SelectItem value="dropdown">Dropdown</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.type === "dropdown" && (
                  <div><Label>Options (comma-separated)</Label><Input placeholder="Small, Medium, Large" value={form.options} onChange={(e) => setForm({ ...form, options: e.target.value })} /></div>
                )}
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.required} onChange={(e) => setForm({ ...form, required: e.target.checked })} />
                  Required
                </label>
                <DialogFooter>
                  <Button type="submit" data-testid="field-submit-btn" className="bg-[#39FF14] hover:bg-[#2ECC71]">Save</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setEntity("customer")}
          data-testid="tab-customer-fields"
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-heading font-medium border ${entity === "customer" ? "bg-[#39FF14] text-[#0B0F0C] border-[#39FF14]/40" : "bg-[#121815] border-[#3A4640] text-[#F8F9FA] hover:bg-[#0B0F0C]"}`}
        >
          <Users size={14} /> Customer Fields
        </button>
        <button
          onClick={() => setEntity("product")}
          data-testid="tab-product-fields"
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-heading font-medium border ${entity === "product" ? "bg-[#39FF14] text-[#0B0F0C] border-[#39FF14]/40" : "bg-[#121815] border-[#3A4640] text-[#F8F9FA] hover:bg-[#0B0F0C]"}`}
        >
          <Package size={14} /> Product Fields
        </button>
      </div>

      <div className="bg-[#121815] border border-[#3A4640] rounded-md shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0B0F0C] border-b border-[#3A4640] text-xs uppercase tracking-wider text-[#8A8F98] font-heading">
            <tr>
              <th className="text-left px-4 py-3">Label</th>
              <th className="text-left px-4 py-3">Key</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">Required</th>
              <th className="text-left px-4 py-3">Options</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="fields-table-body">
            {rows.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-[#8A8F98]"><Settings2 className="mx-auto mb-2" />No custom fields yet.</td></tr>}
            {rows.map((f) => (
              <tr key={f.id} className="border-b border-[#3A4640] last:border-0 hover:bg-[#0B0F0C]">
                <td className="px-4 py-3 font-medium">{f.label}</td>
                <td className="px-4 py-3 font-mono text-xs text-[#8A8F98]">{f.key}</td>
                <td className="px-4 py-3"><span className="text-xs bg-[#121815] px-2 py-1 rounded font-heading">{f.type}</span></td>
                <td className="px-4 py-3">{f.required ? "Yes" : "—"}</td>
                <td className="px-4 py-3 text-xs text-[#8A8F98]">{(f.options || []).join(", ") || "—"}</td>
                <td className="px-4 py-3 text-right">
                  <ConfirmDelete
                    itemLabel={f.label}
                    entityLabel={`${entity} field`}
                    onConfirm={() => del(f)}
                    testid={`field-${f.id}`}
                  >
                    <button className="p-1.5 hover:bg-red-500/10 text-red-600 rounded" data-testid={`delete-field-${f.id}`}><Trash2 size={14} /></button>
                  </ConfirmDelete>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
